import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Trophy, Share2, Loader2 } from 'lucide-react';
import { useAhamMoments, ACHIEVEMENT_CONFIG, AchievementType } from '@/hooks/useAhamMoments';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function AhamMomentToast() {
  const { pendingCelebration, clearCelebration } = useAhamMoments();
  const [isGeneratingCard, setIsGeneratingCard] = useState(false);

  useEffect(() => {
    if (pendingCelebration) {
      const timer = setTimeout(() => {
        clearCelebration();
      }, 8000); // Extended time to allow sharing
      return () => clearTimeout(timer);
    }
  }, [pendingCelebration, clearCelebration]);

  const handleShareToStories = async () => {
    if (!pendingCelebration) return;
    
    const config = ACHIEVEMENT_CONFIG[pendingCelebration];
    setIsGeneratingCard(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-achievement-card', {
        body: {
          achievement_type: pendingCelebration,
          achievement_title: config.title,
          achievement_emoji: config.emoji,
        },
      });

      if (error) throw error;

      if (data?.image_url) {
        // Try to use Web Share API if available (mobile)
        if (navigator.share && navigator.canShare) {
          try {
            // Convert base64 to blob for sharing
            const base64Data = data.image_url.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });
            const file = new File([blob], 'conquista-obrim.png', { type: 'image/png' });

            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: config.title,
                text: `${config.emoji} ${config.title} - Gestão inteligente by Obrim 🚀`,
              });
              toast.success('Card compartilhado!');
              return;
            }
          } catch (shareError) {
            console.log('Web Share failed, falling back to download:', shareError);
          }
        }

        // Fallback: Download the image
        const link = document.createElement('a');
        link.href = data.image_url;
        link.download = `conquista-${pendingCelebration}-obrim.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Card baixado! Agora é só postar nos Stories 📸');
      }
    } catch (error: any) {
      console.error('Error generating card:', error);
      if (error.message?.includes('429') || error.message?.includes('Rate limit')) {
        toast.error('Muitas requisições. Tente novamente em alguns segundos.');
      } else if (error.message?.includes('402')) {
        toast.error('Créditos de IA esgotados.');
      } else {
        toast.error('Erro ao gerar card. Tente novamente.');
      }
    } finally {
      setIsGeneratingCard(false);
    }
  };

  if (!pendingCelebration) return null;

  const config = ACHIEVEMENT_CONFIG[pendingCelebration];

  return (
    <AnimatePresence>
      {pendingCelebration && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 md:bottom-8"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 p-1 shadow-2xl">
            <div className="relative bg-background rounded-xl p-4 pr-10">
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-6 w-6 p-0"
                onClick={clearCelebration}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ rotate: -20, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="text-5xl"
                >
                  {config.emoji}
                </motion.div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Trophy className="h-4 w-4 text-amber-500" />
                    </motion.div>
                    <span className="text-xs font-medium text-amber-500 uppercase tracking-wide">
                      Conquista Desbloqueada!
                    </span>
                  </div>
                  <h3 className="font-bold text-lg">{config.title}</h3>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                  
                  <div className="flex items-center gap-2 pt-1">
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 text-xs font-medium"
                    >
                      +{config.points} pontos
                    </motion.div>
                    
                    {/* Botão Compartilhar no Stories */}
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1 text-primary hover:text-primary"
                        onClick={handleShareToStories}
                        disabled={isGeneratingCard}
                      >
                        {isGeneratingCard ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Share2 className="h-3 w-3" />
                        )}
                        Compartilhar
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
